import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Switch,
  Button,
  Space,
  Divider,
  Tooltip,
  Popconfirm,
} from "antd";
import {
  RiDeleteBinLine,
  RiQuestionLine,
  RiArrowLeftRightLine,
} from "react-icons/ri";
import IndoorPositioningAlertConfig from "../../../../api/IndoorPositioningAlertConfig";
import IndoorPositioningEquipment from "../../../../api/IndoorPositioningEquipment";
import {
  buildTripwirePayload,
  validateTripwireName,
  validateTripwireGeometry,
  DIRECTION_OPTIONS,
  directionHint,
  isSingleDirection,
} from "../utils/tripwireUtils";

// Hover hints for the advanced overrides, refined from the product owner's
// suggested wording (2026-09-22(b) revision).
const ADVANCED_HINTS = {
  band_m:
    "距離線段此範圍內的定位點不判定在哪一側，用來吸收定位誤差造成的抖動。數值越大越不易誤報，但越線判定越慢。",
  confirm_samples: "需連續幾筆定位都落在另一側才算越線。",
  max_gap_s: "越線前後兩筆定位的間隔超過此秒數時，告警會標示為「不確定」。",
  cooldown_seconds: "同一人員／機具對同一條線，在此秒數內只告警一次。",
};

const HintLabel = ({ text, hint }) => (
  <span>
    {text}{" "}
    <Tooltip title={hint}>
      <RiQuestionLine style={{ verticalAlign: "middle", color: "#8c8c8c" }} />
    </Tooltip>
  </span>
);

// Settings form for a single tripwire (name/direction/subjects/severity, and
// per-line overrides that inherit the site alert-config when blank).
// Mirrors the visual language of ProtectionZoneRadiusModal /
// ProtectionZonePersonnelModal (small antd Modal + Form, 確定/取消), rather
// than inventing a new look. Editing only -- creation happens directly from
// the canvas add-tool with backend defaults and never opens this modal (see
// FloorPlanModal/index.js's add-mode flow).
const TripwireSettingsModal = ({
  open,
  tripwire, // existing tripwire being edited, or null
  machineID,
  personnel = [],
  dimensions, // { width, height } -- for the client-side geometry check
  layoutType,
  boundaryArea, // plane walkable-area polygon, for the boundary check
  onClose,
  onSave, // (payload) => Promise<updated|null>
  onDelete, // (id) => Promise<boolean>
  onFlip, // (id, points) => Promise<updated|null> -- 翻轉限制側 (single-direction only)
  saving,
  messageApi, // hook-based antd message instance (message.useMessage()),
  // shared with FloorPlanModal/index.js -- one contextHolder for the whole
  // tripwire feature, mounted there for as long as FloorPlanModal itself is
  // mounted (not just while this settings modal is open). Not the static
  // `message` import -- see useTripwires.js's header comment for why.
}) => {
  const [form] = Form.useForm();
  const [siteDefaults, setSiteDefaults] = useState(null);
  const [equipmentList, setEquipmentList] = useState([]);
  const message = messageApi;

  useEffect(() => {
    if (!open || !machineID) return;
    IndoorPositioningAlertConfig.GetConfig(machineID).then((res) => {
      if (res.code === 200) setSiteDefaults(res.data.config);
    });
    IndoorPositioningEquipment.GetEquipment(machineID).then((res) => {
      if (res.code === 200 && Array.isArray(res.data))
        setEquipmentList(res.data);
    });
  }, [open, machineID]);

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      name: tripwire?.name || "",
      direction: tripwire?.direction || "both",
      applies_to: tripwire?.applies_to || "both",
      severity: tripwire?.severity || "warning",
      enabled: tripwire ? !!tripwire.enabled : true,
      band_m: tripwire?.band_m ?? null,
      confirm_samples: tripwire?.confirm_samples ?? null,
      max_gap_s: tripwire?.max_gap_s ?? null,
      cooldown_seconds: tripwire?.cooldown_seconds ?? null,
      allowed_personnel_ids: tripwire?.allowed_personnel_ids || [],
      allowed_equipment_ids: tripwire?.allowed_equipment_ids || [],
    });
  }, [open, tripwire, form]);

  const appliesTo =
    Form.useWatch("applies_to", form) ?? tripwire?.applies_to ?? "both";
  const showEquipmentHint = appliesTo === "equipment" || appliesTo === "both";

  const direction =
    Form.useWatch("direction", form) ?? tripwire?.direction ?? "both";
  const showFlip = isSingleDirection(direction) && !!onFlip && !!tripwire?.id;

  const handleFlip = async () => {
    if (!tripwire?.id) return;
    await onFlip(tripwire.id, tripwire.points);
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const nameError = validateTripwireName(values.name);
      if (nameError) {
        form.setFields([{ name: "name", errors: [nameError] }]);
        return;
      }
      const geometryError = validateTripwireGeometry(tripwire?.points, {
        floorWidth: dimensions?.width,
        floorHeight: dimensions?.height,
        layoutType,
        boundaryArea,
      });
      if (geometryError) {
        message.error(geometryError);
        return;
      }
      const payload = buildTripwirePayload({
        ...values,
        points: tripwire?.points,
      });
      const result = await onSave(payload);
      if (result) onClose();
    } catch (_) {
      // validation failure or onSave threw -- keep modal open
    }
  };

  return (
    <Modal
      title="越線警報設定"
      open={open}
      onOk={handleOk}
      onCancel={onClose}
      okText="確定"
      cancelText="取消"
      confirmLoading={saving}
      destroyOnClose
      width={520}
    >
      <Form form={form} layout="vertical">
        <Form.Item name="name" label="名稱">
          <Input maxLength={100} placeholder="未命名越線" />
        </Form.Item>

        {/* antd only injects value/onChange into a Form.Item's DIRECT
            child. A bare `<Space.Compact>` there (as this used to be)
            means the Select inside it is never form-bound: selecting a
            new direction doesn't update Form.useWatch (so the hint text
            and 翻轉限制側 visibility go stale) and Save submits the OLD
            direction -- the operator's choice is silently discarded. This
            outer Form.Item is now purely presentational (label + extra,
            no `name`); the inner `noStyle` Form.Item is the one actually
            bound, wrapping only the Select. */}
        <Form.Item label="警報方向" extra={directionHint(direction)}>
          <Space.Compact block>
            <Form.Item name="direction" noStyle>
              <Select style={{ flex: 1 }} options={DIRECTION_OPTIONS} />
            </Form.Item>
            {showFlip && (
              <Button
                icon={<RiArrowLeftRightLine />}
                onClick={handleFlip}
                loading={saving}
              >
                翻轉限制側
              </Button>
            )}
          </Space.Compact>
        </Form.Item>

        <Form.Item
          name="applies_to"
          label="適用對象"
          extra={showEquipmentHint ? "機具任一定位器越線即告警" : undefined}
        >
          <Select
            options={[
              { value: "personnel", label: "人員" },
              { value: "equipment", label: "機具" },
              { value: "both", label: "全部" },
            ]}
          />
        </Form.Item>

        <Form.Item name="severity" label="告警等級">
          <Select
            options={[
              { value: "info", label: "資訊" },
              { value: "warning", label: "警告" },
              { value: "critical", label: "緊急" },
            ]}
          />
        </Form.Item>

        <Form.Item name="enabled" label="啟用" valuePropName="checked">
          <Switch />
        </Form.Item>

        <Divider orientation="left" plain>
          進階設定（留空＝使用預設值）
        </Divider>
        <Space wrap>
          <Form.Item
            name="band_m"
            label={
              <HintLabel text="判定死區 (m)" hint={ADVANCED_HINTS.band_m} />
            }
          >
            <InputNumber
              min={0.1}
              max={10}
              step={0.1}
              placeholder={String(siteDefaults?.tripwire_band_m ?? 1.5)}
            />
          </Form.Item>
          <Form.Item
            name="confirm_samples"
            label={
              <HintLabel
                text="連續確認次數"
                hint={ADVANCED_HINTS.confirm_samples}
              />
            }
          >
            <InputNumber
              min={1}
              max={10}
              placeholder={String(siteDefaults?.tripwire_confirm_samples ?? 2)}
            />
          </Form.Item>
          <Form.Item
            name="max_gap_s"
            label={
              <HintLabel text="最大間隔秒數" hint={ADVANCED_HINTS.max_gap_s} />
            }
          >
            <InputNumber
              min={3}
              max={600}
              placeholder={String(siteDefaults?.tripwire_max_gap_s ?? 30)}
            />
          </Form.Item>
          <Form.Item
            name="cooldown_seconds"
            label={
              <HintLabel
                text="冷卻秒數"
                hint={ADVANCED_HINTS.cooldown_seconds}
              />
            }
          >
            <InputNumber
              min={0}
              max={86400}
              placeholder={String(
                siteDefaults?.tripwire_cooldown_seconds ?? 300,
              )}
            />
          </Form.Item>
        </Space>

        <Divider orientation="left" plain>
          白名單
        </Divider>
        <Form.Item
          name="allowed_personnel_ids"
          label="人員白名單"
          extra="白名單內的人員越線不會告警"
        >
          <Select
            mode="multiple"
            allowClear
            placeholder="未選擇 = 所有人員越線皆告警"
            optionFilterProp="label"
            options={personnel.map((p) => ({ value: p.id, label: p.name }))}
          />
        </Form.Item>
        <Form.Item
          name="allowed_equipment_ids"
          label="機具白名單"
          extra="白名單內的機具越線不會告警"
        >
          <Select
            mode="multiple"
            allowClear
            placeholder="未選擇 = 所有機具越線皆告警"
            optionFilterProp="label"
            options={equipmentList.map((eq) => ({
              value: eq.id,
              label: eq.name,
            }))}
          />
        </Form.Item>

        {tripwire?.id && onDelete && (
          <>
            <Divider />
            <Popconfirm
              title="確認刪除此越線警報？"
              onConfirm={() => onDelete(tripwire.id)}
              okText="刪除"
              cancelText="取消"
            >
              <Button danger block icon={<RiDeleteBinLine />}>
                刪除此越線警報
              </Button>
            </Popconfirm>
          </>
        )}
      </Form>
    </Modal>
  );
};

export default TripwireSettingsModal;
